import Navbar from "@/components/Layout/Navbar";
import Footer from "@/components/Layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  DollarSign, 
  Globe, 
  Shield, 
  Users, 
  Briefcase, 
  CheckCircle2
} from "lucide-react";
import SEOHead from "@/components/SEOHead";
import { PAGE_SEO_CONFIG } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import TrustBar from "@/components/EEAT/TrustBar";
import ArticleMeta from "@/components/EEAT/ArticleMeta";
import AuthorBio from "@/components/EEAT/AuthorBio";

const NearshoringVsOffshoring = () => {
  const { t, i18n } = useTranslation(['nearshoringVsOffshoring', 'common']);
  const seoConfig = PAGE_SEO_CONFIG.nearshoringVsOffshoring;
  const currentLang = i18n.language;

  const benefits = [
    { icon: Globe, title: t('nearshoringVsOffshoring:benefits.proximity.title'), description: t('nearshoringVsOffshoring:benefits.proximity.description') },
    { icon: Briefcase, title: t('nearshoringVsOffshoring:benefits.travel.title'), description: t('nearshoringVsOffshoring:benefits.travel.description') },
    { icon: Shield, title: t('nearshoringVsOffshoring:benefits.quality.title'), description: t('nearshoringVsOffshoring:benefits.quality.description') },
    { icon: Users, title: t('nearshoringVsOffshoring:benefits.talent.title'), description: t('nearshoringVsOffshoring:benefits.talent.description') },
  ];

  const skillsGapBenefits = [
    { title: t('nearshoringVsOffshoring:skillsGap.access.title'), description: t('nearshoringVsOffshoring:skillsGap.access.description') },
    { title: t('nearshoringVsOffshoring:skillsGap.finding.title'), description: t('nearshoringVsOffshoring:skillsGap.finding.description') },
    { title: t('nearshoringVsOffshoring:skillsGap.costEffective.title'), description: t('nearshoringVsOffshoring:skillsGap.costEffective.description') },
    { title: t('nearshoringVsOffshoring:skillsGap.integration.title'), description: t('nearshoringVsOffshoring:skillsGap.integration.description') },
  ];

  const keyBenefits = [
    { icon: DollarSign, title: t('nearshoringVsOffshoring:keyBenefits.costSavings.title'), description: t('nearshoringVsOffshoring:keyBenefits.costSavings.description') },
    { icon: Users, title: t('nearshoringVsOffshoring:keyBenefits.skillsAccess.title'), description: t('nearshoringVsOffshoring:keyBenefits.skillsAccess.description') },
    { icon: Shield, title: t('nearshoringVsOffshoring:keyBenefits.secure.title'), description: t('nearshoringVsOffshoring:keyBenefits.secure.description') },
  ];

  const stats = [
    { value: "500+", label: t('nearshoringVsOffshoring:successStories.stats.projects') },
    { value: "98%", label: t('nearshoringVsOffshoring:successStories.stats.clients') },
    { value: "$1M+", label: t('nearshoringVsOffshoring:successStories.stats.savings') },
    { value: "200+", label: t('nearshoringVsOffshoring:successStories.stats.experts') },
  ];

  const faqs = [
    { question: t('nearshoringVsOffshoring:faq.q1.question'), answer: t('nearshoringVsOffshoring:faq.q1.answer') },
    { question: t('nearshoringVsOffshoring:faq.q2.question'), answer: t('nearshoringVsOffshoring:faq.q2.answer') },
    { question: t('nearshoringVsOffshoring:faq.q3.question'), answer: t('nearshoringVsOffshoring:faq.q3.answer') },
    { question: t('nearshoringVsOffshoring:faq.q4.question'), answer: t('nearshoringVsOffshoring:faq.q4.answer') },
  ];

  const evolviceUrl = currentLang === 'de' ? 'https://www.evolvice.de' : 'https://www.evolvice.de/en';

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        titleEn={seoConfig.titleEn}
        titleDe={seoConfig.titleDe}
        descriptionEn={seoConfig.descriptionEn}
        descriptionDe={seoConfig.descriptionDe}
        keywords={seoConfig.keywords}
        path={seoConfig.path}
        breadcrumbs={[
          { name: 'Home', nameDE: 'Startseite', url: '/' },
          { name: 'Nearshoring vs Offshoring', nameDE: 'Nearshoring vs Offshoring', url: '/nearshoring-vs-offshoring-vs-outsourcing' }
        ]}
        faqs={faqs}
      />
      <Navbar />
      <main className="pt-24">
        {/* Hero */}
        <section className="py-32">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Comparison</div>
              <h1 className="text-5xl md:text-6xl font-semibold tracking-[-0.035em] text-balance mb-6">
                {t('nearshoringVsOffshoring:hero.title')}
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                {t('nearshoringVsOffshoring:hero.subtitle')}
              </p>
              <div className="mt-8 flex justify-center">
                <ArticleMeta publishedDate="2025-09-15" updatedDate="2026-04-19" />
              </div>
            </div>
          </div>
        </section>

        {/* Trust */}
        <TrustBar />

        {/* Cost Savings Banner */}
        <section className="pb-24">
          <div className="container mx-auto px-6">
            <Card className="max-w-4xl mx-auto">
              <CardContent className="p-10 text-center">
                <h2 className="text-2xl md:text-3xl font-semibold tracking-[-0.028em]">
                  {t('nearshoringVsOffshoring:costSavings.title')}
                </h2>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Benefits Grid */}
        <section className="pb-32">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-5 max-w-6xl mx-auto">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card key={index} className="group">
                    <CardContent className="p-8">
                      <Icon className="w-5 h-5 text-muted-foreground mb-5" aria-hidden="true" />
                      <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3">{benefit.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Skills Gap */}
        <section className="py-32 border-t border-border/60">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">Skills</div>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance mb-4">
                  {t('nearshoringVsOffshoring:skillsGap.title')}
                </h2>
                <p className="text-lg text-muted-foreground">{t('nearshoringVsOffshoring:skillsGap.subtitle')}</p>
              </div>
              <p className="text-lg text-foreground/80 mb-10 text-center max-w-3xl mx-auto leading-relaxed">
                {t('nearshoringVsOffshoring:skillsGap.description')}
              </p>
              <div className="space-y-4">
                {skillsGapBenefits.map((benefit, index) => (
                  <Card key={index}>
                    <CardContent className="p-7">
                      <h3 className="text-lg font-semibold tracking-[-0.02em] mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-32 border-t border-border/60">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">Why</div>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance mb-4">
                {t('nearshoringVsOffshoring:keyBenefits.title')}
              </h2>
              <p className="text-lg text-muted-foreground">{t('nearshoringVsOffshoring:keyBenefits.subtitle')}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {keyBenefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card key={index}>
                    <CardContent className="p-8">
                      <Icon className="w-5 h-5 text-muted-foreground mb-5" aria-hidden="true" />
                      <h3 className="text-xl font-semibold tracking-[-0.02em] mb-3">{benefit.title}</h3>
                      <p className="text-muted-foreground mb-6 leading-relaxed">{benefit.description}</p>
                      <Button variant="glass" size="sm" asChild>
                        <a href={evolviceUrl} target="_blank" rel="noopener noreferrer">
                          {t('common:buttons.learnMore')}
                        </a>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Success Stories */}
        <section className="py-32 border-t border-border/60">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto text-center">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">Proof</div>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance mb-4">
                {t('nearshoringVsOffshoring:successStories.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-16">
                {t('nearshoringVsOffshoring:successStories.subtitle')}
              </p>
              <Card className="mb-12">
                <CardContent className="p-10">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    {stats.map((stat, index) => (
                      <div key={index} className="text-center">
                        <div className="text-4xl md:text-5xl font-semibold tracking-[-0.02em] mb-2">
                          {stat.value}
                        </div>
                        <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Button size="lg" variant="glass" asChild>
                <a href={evolviceUrl} target="_blank" rel="noopener noreferrer">
                  {t('common:buttons.learnMore')}
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Author */}
        <section className="py-16 border-t border-border/60">
          <div className="container mx-auto px-6 max-w-3xl">
            <AuthorBio
              authorId="sherif-adel"
              reviewedByDe="Evolvice Editorial Team"
              reviewedByEn="Evolvice Editorial Team"
            />
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 border-t border-border/60">
          <div className="container mx-auto px-6">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance mb-6">
                {t('nearshoringVsOffshoring:cta.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                {t('nearshoringVsOffshoring:cta.subtitle')}
              </p>
              <Button size="lg" asChild>
                <a href={evolviceUrl} target="_blank" rel="noopener noreferrer">
                  {t('nearshoringVsOffshoring:cta.button')}
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-32 border-t border-border/60">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">FAQ</div>
                <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.028em] text-balance mb-4">
                  {t('nearshoringVsOffshoring:faq.title')}
                </h2>
                <p className="text-lg text-muted-foreground">{t('nearshoringVsOffshoring:faq.subtitle')}</p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="p-7">
                      <h3 className="text-lg font-semibold tracking-[-0.02em] mb-3 flex items-start gap-3">
                        <CheckCircle2 className="text-foreground/40 flex-shrink-0 mt-1" size={18} />
                        {faq.question}
                      </h3>
                      <p className="text-muted-foreground ml-8 leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default NearshoringVsOffshoring;
